import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import Icon from "@mdi/react";
import { Alert, Breadcrumb, Card, Segmented, Slider, Spin } from "antd";
import { observer } from "mobx-react-lite";

import {
  getSubscriptionInAdvance,
  getSubscriptionPlanPrice,
  getSubscriptionPlans,
  mapSubscriptionPlan,
} from "../api/subscription";
import { SubscriptionManageCheckout } from "../components/subscription/SubscriptionManageCheckout";
import { userStore } from "../stores/userStore";
import { sendAnalyticsEvent } from "../utils/counterService";
import { formatDate } from "../utils/date";
import { usePageTitle } from "../utils/usePageTitle";

import "./SubscriptionManagePage.css";
import "./SubscriptionUpdatePage.css";

import type {
  PriceInfo,
  SubscriptionInAdvance,
  SubscriptionPlan,
} from "../api/subscription";

const USER_LIMIT_MIN = 5;
const USER_LIMIT_MAX = 100;
const STORAGE_LIMIT_MIN_GB = 50;
const STORAGE_LIMIT_MAX_GB = 250;
const STORAGE_LIMIT_STEP_GB = 50;
const MINI_USER_LIMIT = 1;
const MINI_STORAGE_LIMIT_GB = 10;
const BYTES_IN_GB = 1024 ** 3;
const QUOTE_DEBOUNCE_MS = 500;
const TWEAK_EVENT_DEBOUNCE_MS = 60_000;
const TWEAK_EVENT_NAME = "pomelo_config";
const USER_LIMIT_VALUES = [
  ...Array.from({ length: 6 }, (_, index) => USER_LIMIT_MIN + index),
  ...Array.from({ length: 18 }, (_, index) => 15 + index * 5),
];
const STORAGE_LIMIT_VALUES_GB = Array.from(
  {
    length:
      (STORAGE_LIMIT_MAX_GB - STORAGE_LIMIT_MIN_GB) / STORAGE_LIMIT_STEP_GB + 1,
  },
  (_, index) => STORAGE_LIMIT_MIN_GB + index * STORAGE_LIMIT_STEP_GB,
);

function parseLimitParam(
  value: string | null,
  allowedValues: number[],
  lowerBound: number,
) {
  if (value === null || !/^\d+$/.test(value)) return null;
  const limit = Number(value);
  return limit >= lowerBound && allowedValues.includes(limit) ? limit : null;
}

interface ManageQuote {
  plan: SubscriptionPlan;
  configuredPrice: PriceInfo;
  configuredOriginalPrice: PriceInfo;
  inAdvance: SubscriptionInAdvance;
  period: "monthly" | "yearly";
  settings: {
    user_limit: number;
    storage_limit: number;
  };
}

interface Props {
  initialPlan?: "mandarin" | "pomelo";
  initialPeriod?: "monthly" | "yearly";
  onPaymentComplete?: (url: string) => void;
}

export const SubscriptionManagePage = observer(function SubscriptionManagePage(
  props: Props = {},
) {
  const [searchParams] = useSearchParams();
  const requestedPlan = searchParams.get("plan")?.toLowerCase();
  const plan =
    (
      { free: "lemon", mini: "mandarin", premium: "pomelo" } as Record<
        string,
        string
      >
    )[requestedPlan ?? ""] ?? requestedPlan;
  const isTrial = searchParams.get("trial") === "true";

  if (isTrial && plan !== "pomelo")
    return <Navigate to="/subscription/update/plans" replace />;
  if (
    isTrial &&
    (!userStore.subscription?.allow_trial ||
      userStore.subscription?.autopayment ||
      userStore.subscription?.trial_active)
  ) {
    return <Navigate to="/subscription" replace />;
  }
  if (plan === "lemon")
    return <Navigate to="/subscription/update/cancel" replace />;
  if (plan && plan !== "mandarin" && plan !== "pomelo")
    return <Navigate to="/subscription/update/plans" replace />;

  // Reset checkout state when switching between trial and paid subscriptions.
  return (
    <SubscriptionManagement
      key={String(isTrial)}
      {...props}
      requestedPlan={plan}
      isTrial={isTrial}
    />
  );
});

const SubscriptionManagement = observer(function SubscriptionManagement({
  initialPlan,
  initialPeriod,
  onPaymentComplete,
  requestedPlan,
  isTrial,
}: Props & { requestedPlan?: string; isTrial: boolean }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const [searchParams, setSearchParams] = useSearchParams();
  const usersParam = searchParams.get("users");
  const storageParam = searchParams.get("storage");
  const planParam = requestedPlan;
  const requestedPeriod =
    searchParams.get("period") ?? searchParams.get("interval");
  const periodParam =
    requestedPeriod === "annually" || requestedPeriod === "year"
      ? "yearly"
      : requestedPeriod;
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const currentPlan = userStore.subscription?.plan?.toLowerCase();
  const defaultPlanId =
    initialPlan ??
    (currentPlan === "mandarin" || currentPlan === "pomelo"
      ? currentPlan
      : "pomelo");
  const requestedPlanId =
    planParam === "mandarin" || planParam === "pomelo"
      ? planParam
      : defaultPlanId;
  const selectedPlanId =
    !loading && !plans.some((candidate) => candidate.id === requestedPlanId)
      ? (plans[0]?.id ?? "pomelo")
      : requestedPlanId;
  const period =
    isTrial
      ? "monthly"
      : periodParam === "monthly" || periodParam === "yearly"
        ? periodParam
        : (initialPeriod ??
          (userStore.subscription?.interval === "yearly" ? "yearly" : "monthly"));

  const userLimitLowerBound = Math.max(
    userStore.subscription?.user_settings?.user_limit ?? USER_LIMIT_MIN,
    USER_LIMIT_MIN,
  );
  const storageLimitLowerBoundGb = Math.max(
    (userStore.subscription?.user_settings?.storage_limit ??
      STORAGE_LIMIT_MIN_GB * BYTES_IN_GB) / BYTES_IN_GB,
    STORAGE_LIMIT_MIN_GB,
  );

  const [quote, setQuote] = useState<ManageQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [showLoweringAlert, setShowLoweringAlert] = useState(false);
  const tweakEventDebounce = useRef<number | undefined>(undefined);
  const plan =
    plans.find((candidate) => candidate.id === selectedPlanId) ?? null;

  const parsedUsers = parseLimitParam(
    usersParam,
    [...USER_LIMIT_VALUES, userLimitLowerBound],
    userLimitLowerBound,
  );
  const parsedStorage = parseLimitParam(
    storageParam,
    [...STORAGE_LIMIT_VALUES_GB, storageLimitLowerBoundGb],
    storageLimitLowerBoundGb,
  );
  const userLimit = parsedUsers ?? userLimitLowerBound;
  const storageLimitGb = parsedStorage ?? storageLimitLowerBoundGb;

  const userLimitValues = useMemo(
    () =>
      [...new Set([...USER_LIMIT_VALUES, userLimit, userLimitLowerBound])].sort(
        (left, right) => left - right,
      ),
    [userLimit, userLimitLowerBound],
  );

  const userLimitIndex = userLimitValues.indexOf(userLimit);
  const userLimitLowerBoundIndex = userLimitValues.indexOf(userLimitLowerBound);

  usePageTitle(t("subscription.changePlan"));

  const settings = useMemo(
    () =>
      isTrial
        ? {
            user_limit: USER_LIMIT_MIN,
            storage_limit: STORAGE_LIMIT_MIN_GB * BYTES_IN_GB,
          }
        : selectedPlanId === "mandarin"
          ? {
              user_limit: MINI_USER_LIMIT,
              storage_limit: MINI_STORAGE_LIMIT_GB * BYTES_IN_GB,
            }
          : {
              user_limit: userLimit,
              storage_limit: storageLimitGb * BYTES_IN_GB,
            },
    [isTrial, selectedPlanId, storageLimitGb, userLimit],
  );

  useEffect(() => {
    if (loading) return;
    const nextParams = new URLSearchParams(searchParams);
    if (planParam !== selectedPlanId) nextParams.delete("plan");
    if (periodParam !== period) nextParams.delete("period");
    if (selectedPlanId !== "pomelo" || parsedUsers === null) {
      nextParams.delete("users");
    }
    if (selectedPlanId !== "pomelo" || parsedStorage === null) {
      nextParams.delete("storage");
    }
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    loading,
    parsedStorage,
    parsedUsers,
    period,
    periodParam,
    planParam,
    searchParams,
    selectedPlanId,
    setSearchParams,
  ]);

  const updateConfiguration = (changes: {
    plan?: string;
    period?: "monthly" | "yearly";
    users?: number;
    storage?: number;
  }) => {
    const nextParams = new URLSearchParams(searchParams);
    const nextPlan = changes.plan ?? selectedPlanId;
    nextParams.set("plan", nextPlan);
    nextParams.set("period", changes.period ?? period);
    if (nextPlan === "pomelo") {
      nextParams.set("users", String(changes.users ?? userLimit));
      nextParams.set("storage", String(changes.storage ?? storageLimitGb));
    } else {
      nextParams.delete("users");
      nextParams.delete("storage");
    }
    // Commit each slider change before another control reads the URL state.
    setSearchParams(nextParams, {
      replace: true,
      flushSync: true,
      preventScrollReset: true,
    });
  };

  useEffect(() => {
    let active = true;
    getSubscriptionPlans()
      .then((result) => {
        if (!active) return;
        const paidPlans = result
          .filter(
            (item) =>
              item.id === "pomelo" || (!isTrial && item.id === "mandarin"),
          )
          .map((item) => mapSubscriptionPlan(item, userStore.currency));
        setPlans(paidPlans);
        setLoading(false);
        setLoadError(paidPlans.length === 0);
      })
      .catch((error) => {
        if (active && error.name !== "AbortError") {
          setLoading(false);
          setLoadError(true);
        }
      });
    return () => {
      active = false;
    };
  }, [isTrial]);

  useEffect(() => {
    if (!plan) return;
    const controller = new AbortController();
    setQuoteLoading(true);
    setLoadError(false);
    const timer = window.setTimeout(() => {
      Promise.all([
        getSubscriptionPlanPrice({
          plan: plan.id,
          currency: userStore.currency,
          interval: period,
          settings,
          signal: controller.signal,
        }),
        getSubscriptionInAdvance({
          plan: plan.id,
          interval: period,
          settings,
          signal: controller.signal,
        }),
      ])
        .then(([priceResult, inAdvanceResult]) => {
          if (controller.signal.aborted) return;
          const currency = userStore.currency;
          const curr = currency.toLowerCase();
          const amount =
            priceResult.price_discounted?.[period]?.[curr] ??
            priceResult.price_original?.[period]?.[curr];
          if (amount == null) throw new Error("Price is missing");
          const originalAmount =
            priceResult.price_original?.[period]?.[curr] ?? amount;
          setQuote({
            plan,
            configuredPrice: { amount: Number(amount), currency },
            configuredOriginalPrice: {
              amount: Number(originalAmount),
              currency,
            },
            inAdvance: inAdvanceResult,
            period,
            settings,
          });
          setQuoteLoading(false);
        })
        .catch((error) => {
          if (!controller.signal.aborted && error.name !== "AbortError") {
            setQuote(null);
            setQuoteLoading(false);
            setLoadError(true);
          }
        });
    }, QUOTE_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [period, plan, settings]);

  const handleSliderTweak = useCallback(() => {
    if (tweakEventDebounce.current === undefined) {
      // console.log(`[analytics] ${TWEAK_EVENT_NAME}: fired`);
      sendAnalyticsEvent(TWEAK_EVENT_NAME);
    } else {
      // console.log(
      //   `[analytics] ${TWEAK_EVENT_NAME}: debounced; inactivity window reset to ${TWEAK_EVENT_DEBOUNCE_MS}ms`,
      // );
      window.clearTimeout(tweakEventDebounce.current);
    }

    tweakEventDebounce.current = window.setTimeout(() => {
      tweakEventDebounce.current = undefined;
      // console.log(
      //   `[analytics] ${TWEAK_EVENT_NAME}: debounce window expired; next tweak will fire`,
      // );
    }, TWEAK_EVENT_DEBOUNCE_MS);
  }, []);

  useEffect(
    () => () => {
      if (tweakEventDebounce.current !== undefined) {
        window.clearTimeout(tweakEventDebounce.current);
      }
    },
    [],
  );

  const configuration = isTrial ? (
    <Card className="subscription-manage__card subscription-manage__trial-configuration">
      <SectionTitle>{t("subscription.configuration")}</SectionTitle>
      <FixedLimit
        label={t("subscription.limitUsers")}
        value={t("subscription.planDescriptions.users", {
          count: USER_LIMIT_MIN,
          userCount: USER_LIMIT_MIN,
        })}
      />
      <FixedLimit
        label={t("subscription.limitStorage")}
        value={`${STORAGE_LIMIT_MIN_GB} GB`}
      />
    </Card>
  ) : (
    <>
      <Card className="subscription-manage__card subscription-manage__configuration">
        <div className="subscription-manage__selection-row">
          <div className="subscription-manage__selection-group">
            <SectionTitle>{t("subscription.plan")}</SectionTitle>
            <Segmented
              block
              size="large"
              value={selectedPlanId}
              onChange={(value) => {
                updateConfiguration({ plan: String(value) });
                setShowLoweringAlert(false);
              }}
              options={plans.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
            />
          </div>

          <div className="subscription-manage__selection-group">
            <SectionTitle>{t("subscription.billingPeriod")}</SectionTitle>
            <Segmented
              block
              size="large"
              className="subscription-manage__period-switch"
              value={period}
              onChange={(value) =>
                updateConfiguration({ period: value as "monthly" | "yearly" })
              }
              options={[
                { value: "monthly", label: t("subscription.monthly") },
                {
                  value: "yearly",
                  label: (
                    <span className="subscription-manage__period-label">
                      {t("subscription.yearly")}
                      {plan?.discount_annual && (
                        <span className="subscription-manage__discount">
                          {`-${plan.discount_annual}%`}
                        </span>
                      )}
                    </span>
                  ),
                },
              ]}
            />
          </div>
        </div>
        <SectionTitle>{t("subscription.configuration")}</SectionTitle>
        {selectedPlanId === "mandarin" ? (
          <div className="subscription-manage__fixed-limits">
            <FixedLimit
              label={t("subscription.limitUsers")}
              value={t("subscription.planDescriptions.users", {
                count: MINI_USER_LIMIT,
                userCount: MINI_USER_LIMIT,
              })}
            />
            <FixedLimit
              label={t("subscription.limitStorage")}
              value={`${MINI_STORAGE_LIMIT_GB} GB`}
            />
            <p>{t("subscription.miniFixedLimits")}</p>
          </div>
        ) : (
          <>
            <LimitSlider
              label={t("subscription.limitUsers")}
              valueLabel={t("subscription.planDescriptions.users", {
                count: settings.user_limit,
                userCount: settings.user_limit,
              })}
              value={userLimitIndex}
              min={0}
              max={userLimitValues.length - 1}
              step={1}
              minLabel={String(USER_LIMIT_MIN)}
              maxLabel={String(USER_LIMIT_MAX)}
              lowerBound={userLimitLowerBoundIndex}
              onTweak={handleSliderTweak}
              onLowerAttempt={() => setShowLoweringAlert(true)}
              onValidChange={() => setShowLoweringAlert(false)}
              onChange={(index) =>
                updateConfiguration({ users: userLimitValues[index] })
              }
            />
            <LimitSlider
              label={t("subscription.limitStorage")}
              valueLabel={`${storageLimitGb} GB`}
              value={storageLimitGb}
              min={STORAGE_LIMIT_MIN_GB}
              max={STORAGE_LIMIT_MAX_GB}
              step={STORAGE_LIMIT_STEP_GB}
              minLabel={`${STORAGE_LIMIT_MIN_GB} GB`}
              maxLabel={`${STORAGE_LIMIT_MAX_GB} GB`}
              lowerBound={storageLimitLowerBoundGb}
              onTweak={handleSliderTweak}
              onLowerAttempt={() => setShowLoweringAlert(true)}
              onValidChange={() => setShowLoweringAlert(false)}
              onChange={(value) => updateConfiguration({ storage: value })}
            />
          </>
        )}
      </Card>
      {showLoweringAlert && (
        <Alert
          className="subscription-manage__lowering-alert"
          type="warning"
          showIcon
          title={t("subscription.lowerLimitsNotAllowed")}
          closable={{ afterClose: () => setShowLoweringAlert(false) }}
        />
      )}
    </>
  );

  return (
    <div className="page subscription-update-page subscription-manage-page">
      <Breadcrumb
        className="subscription-update-page__breadcrumb"
        separator={
          <Icon path={isRtl ? mdiChevronLeft : mdiChevronRight} size={0.7} />
        }
        items={[
          {
            title: <Link to="/subscription">{t("subscription.title")}</Link>,
          },
          { title: t("subscription.changePlan") },
        ]}
      />
      <h1>{t("subscription.changePlan")}</h1>

      {isTrial && quote && (
        <Alert
          className="subscription-update-page__trial-alert"
          type="info"
          showIcon
          icon={<span>🎁</span>}
          title={t("subscription.trial.checkout.title")}
          description={
            <Trans
              i18nKey="subscription.trial.checkout.desc"
              values={{
                date: formatDate(
                  quote.inAdvance.subscription.expire_at,
                  i18n.language,
                ),
              }}
              components={{ b: <strong /> }}
            />
          }
        />
      )}
      {loadError ? (
        <Alert type="error" showIcon title={t("errors.shared.common_error")} />
      ) : loading || !plan || !quote ? (
        <div className="subscription-update-page__loading">
          <Spin size="large" />
        </div>
      ) : (
        <SubscriptionManageCheckout
          selectedPlan={quote.plan}
          selectedPeriod={quote.period}
          inAdvance={quote.inAdvance}
          settings={quote.settings}
          configuredPrice={quote.configuredPrice}
          configuredOriginalPrice={quote.configuredOriginalPrice}
          configuration={configuration}
          onPaymentComplete={onPaymentComplete}
          isTrial={isTrial}
          refreshing={
            quoteLoading ||
            quote.plan.id !== selectedPlanId ||
            quote.period !== period ||
            quote.settings.user_limit !== settings.user_limit ||
            quote.settings.storage_limit !== settings.storage_limit
          }
        />
      )}
    </div>
  );
});

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div className="subscription-manage__section-title">{children}</div>;
}

function FixedLimit({ label, value }: { label: string; value: string }) {
  return (
    <div className="subscription-manage__fixed-limit">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function LimitSlider({
  label,
  valueLabel,
  value,
  min,
  max,
  step,
  minLabel,
  maxLabel,
  lowerBound,
  onTweak,
  onLowerAttempt,
  onValidChange,
  onChange,
}: {
  label: string;
  valueLabel: string;
  value: number;
  min: number;
  max: number;
  step: number;
  minLabel: string;
  maxLabel: string;
  lowerBound: number;
  onTweak: () => void;
  onLowerAttempt: () => void;
  onValidChange: () => void;
  onChange: (value: number) => void;
}) {
  const handleChange = (nextValue: number) => {
    onTweak();
    if (nextValue < lowerBound) {
      onLowerAttempt();
      onChange(lowerBound);
      return;
    }
    onValidChange();
    onChange(nextValue);
  };

  return (
    <div className="subscription-manage__limit">
      <div className="subscription-manage__limit-header">
        <strong>{label}</strong>
        <span>{valueLabel}</span>
      </div>
      <Slider
        min={min}
        max={max}
        step={step}
        value={value}
        ariaLabelForHandle={label}
        ariaValueTextFormatterForHandle={() => valueLabel}
        tooltip={{ formatter: () => valueLabel }}
        onChange={handleChange}
      />
      <div className="subscription-manage__limit-range">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
}
